import { useState, useEffect, useCallback } from "react";
import {
  getExpenseBootstrapData,
  getExpenseBootstrapError,
  getExpenseBootstrapPromise,
} from "../state/expenseBootstrapStore";
import { useExpenseBootstrap } from "./useExpenseBootstrap";
import { formatPlAmount } from "../utils/formatPlAmount";

function formatAverageLabel(averageExpenseRupees: number): string {
  return `Avg ${formatPlAmount(averageExpenseRupees)}`;
}

export function useTwelveMonthExpenseTrend() {
  useExpenseBootstrap();

  const [labels, setLabels] = useState<string[]>([]);
  const [values, setValues] = useState<number[]>([]);
  const [averageExpense, setAverageExpense] = useState<number>(0);
  const [averageLabel, setAverageLabel] = useState<string>("");
  const [rangeLabel, setRangeLabel] = useState<string>("");
  const [monthCount, setMonthCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const syncFromBootstrap = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let bootstrapEntity = getExpenseBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getExpenseBootstrapPromise();
        if (bootstrapPromise) {
          bootstrapEntity = await bootstrapPromise;
        }
      }

      const bootstrapError = getExpenseBootstrapError();
      if (!bootstrapEntity) {
        throw new Error(bootstrapError ?? "Expense bootstrap data unavailable");
      }

      const section = bootstrapEntity.sections.twelveMonthTrend;
      if (section.status !== "ok" || !section.data) {
        throw new Error(section.error ?? "Monthly trend unavailable");
      }

      const entity = section.data;
      setLabels(entity.labels);
      setValues(entity.values);
      setAverageExpense(entity.averageExpense);
      setAverageLabel(formatAverageLabel(entity.averageExpense));
      setRangeLabel(entity.rangeLabel);
      setMonthCount(entity.monthCount);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to load monthly trend";
      setError(message);
      setLabels([]);
      setValues([]);
      setAverageExpense(0);
      setAverageLabel("");
      setRangeLabel("");
      setMonthCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void syncFromBootstrap();
  }, [syncFromBootstrap]);

  return {
    labels,
    values,
    averageExpense,
    averageLabel,
    rangeLabel,
    monthCount,
    loading,
    error,
    refetch: syncFromBootstrap,
  };
}
